import type { ScenarioStepV1 } from 'api/scenarios';

/** 図の寸法。DOMを測らずに座標を決められるよう固定値にする。 */
export const NODE_WIDTH = 288;
export const NODE_GAP = 96;
export const NODE_HEIGHT = 76;
export const LANE_HEIGHT = 34;
/** 枝を1段下げるときの縦の間隔。 */
export const ROW_HEIGHT = 116;
/** 端の開始・完了マーカーの幅。 */
export const MARKER_WIDTH = 112;

export type FlowJump = {
  from: number;
  to: number;
  /** 飛び越されて送られないステップ番号（1始まり）。 */
  skipped: number[];
  /** 重ならないように割り当てた縦位置。0が図に一番近い。 */
  lane: number;
  description: string;
};

/** ステップi（0始まり）の左端X座標。 */
export const nodeX = (index: number): number => index * (NODE_WIDTH + NODE_GAP);
/** ステップiの中心X座標。 */
export const nodeCenterX = (index: number): number => nodeX(index) + NODE_WIDTH / 2;

/**
 * 条件不成立の飛び先を、重ならないレーンへ割り当てて返す。
 * 区間が重なる分岐は別のレーンへ逃がし、線が重なって読めなくなるのを防ぐ。
 */
export function buildJumps(steps: readonly ScenarioStepV1[]): FlowJump[] {
  const lanes: number[] = [];
  return steps.flatMap((step, from) => {
    if (!step.condition || !step.falseTargetStepId) return [];
    const to = steps.findIndex(({ id }) => id === step.falseTargetStepId);
    if (to < 0) return [];
    const skipped = steps.slice(from, to).map((_, offset) => from + offset + 1);
    // 既に使われているレーンのうち、この区間と重ならない一番手前を使う
    let lane = lanes.findIndex((usedUntil) => usedUntil <= from);
    if (lane < 0) lane = lanes.length;
    lanes[lane] = to;
    return [{
      from,
      to,
      skipped,
      lane,
      description: `ステップ${to + 1}へ進みます（ステップ${skipped.join('・')}は送りません）`,
    }];
  });
}

/** 分岐の線が使う縦方向の高さ。分岐が無ければ0。 */
export const bandHeight = (jumps: readonly FlowJump[]): number =>
  jumps.length ? (Math.max(...jumps.map(({ lane }) => lane)) + 1) * LANE_HEIGHT + 12 : 0;

/**
 * 飛び元の上辺から立ち上がり、レーンを横切って飛び先の上辺へ降りる折れ線。
 * @param jump 対象の分岐
 * @param band 分岐帯の高さ
 * @returns SVGのpath d属性
 */
export function jumpPath(jump: FlowJump, band: number, fromY: number, toY: number): string {
  const y = band - (jump.lane + 1) * LANE_HEIGHT;
  const startX = nodeCenterX(jump.from);
  const endX = nodeCenterX(jump.to);
  // 縦の区間はステップ自身の列を通るので、段が違っても他のノードとぶつからない
  return `M ${startX} ${fromY} V ${y + 8} Q ${startX} ${y} ${startX + 12} ${y} H ${endX - 12} Q ${endX} ${y} ${endX} ${y + 8} V ${toY}`;
}

/**
 * 各ステップの段。条件を満たしたときだけ通る区間を1段下げ、分岐を枝として見せる。
 * 入れ子になった分岐はさらに下の段へ降りる。
 * @param steps 表示順のstep
 * @param jumps 条件不成立の飛び先
 * @returns stepごとの段（0が本線）
 */
export function depthsOf(steps: readonly unknown[], jumps: readonly FlowJump[]): number[] {
  return steps.map((_, index) => jumps.filter(({ from, to }) => from < index && index < to).length);
}

/** ノードの上端Y座標。 */
export const nodeY = (depth: number, band: number): number => band + depth * ROW_HEIGHT;

/**
 * 段が変わるステップ間を、直角に折れる線でつなぐ。同じ段なら水平線にする。
 * @returns SVGのpath d属性
 */
export function elbowPath(x1: number, y1: number, x2: number, y2: number): string {
  if (y1 === y2) return `M ${x1} ${y1} H ${x2}`;
  const midX = x1 + (x2 - x1) / 2;
  const down = y2 > y1 ? 1 : -1;
  return `M ${x1} ${y1} H ${midX - 10} Q ${midX} ${y1} ${midX} ${y1 + down * 10} V ${y2 - down * 10} Q ${midX} ${y2} ${midX + 10} ${y2} H ${x2}`;
}
